import React, { useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import {
  ArrowLeft,
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  Check,
  AlertCircle,
  Info,
  Loader2,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "react-toastify";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import { ImageUpload, SuccessModal } from "./components";
import organizerService from "../../../../api/OrganizerService";

const normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;
  
  let cleaned = phoneNumber.replace(/[\s\-()]/g, "");
  
  if (cleaned.startsWith("0")) {
    cleaned = "233" + cleaned.substring(1);
  }
  else if (cleaned.startsWith("+233")) {
    cleaned = cleaned.substring(1);
  }
  
  return cleaned;
};

/**
 * Form Validation Schema
 */
const validate = (values) => {
  const errors = {};

  // Name validation
  if (!values.name) {
    errors.name = "Organizer name is required";
  } else if (values.name.length < 3) {
    errors.name = "Name must be at least 3 characters";
  } else if (values.name.length > 100) {
    errors.name = "Name must not exceed 100 characters";
  }

  // Phone validation
  if (!values.phoneNumber) {
    errors.phoneNumber = "Phone number is required";
  } else {
    const cleanedNumber = values.phoneNumber.replace(/[\s\-()]/g, "");
    
    const isValidInternational = isPossiblePhoneNumber(cleanedNumber);
    const isValidLocalFormat = /^(\+?[0-9]\d{6,15})$/.test(cleanedNumber);
    
    if (!isValidInternational && !isValidLocalFormat) {
      errors.phoneNumber = "Please enter a valid phone number";
    } else if (cleanedNumber.length < 7) {
      errors.phoneNumber = "Phone number is too short";
    } else if (cleanedNumber.length > 16) {
      errors.phoneNumber = "Phone number is too long";
    }
  }

  // Email validation
  if (!values.email) {
    errors.email = "Email address is required";
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }

  // Country validation
  if (!values.country) {
    errors.country = "Country is required";
  }

  // City validation
  if (!values.city) {
    errors.city = "City is required";
  } else if (values.city.length < 2) {
    errors.city = "City must be at least 2 characters";
  }

  return errors;
};

/**
 * Main Component
 */
const CreateEditOrganizer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const isEditMode = Boolean(id);
  const existingOrganizer = location.state?.organizer;

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [savedOrganizerName, setSavedOrganizerName] = useState("");
  const logoChangedRef = useRef(false);

  // Formik setup
  const formik = useFormik({
    initialValues: {
      name: existingOrganizer?.name || "",
      phoneNumber: existingOrganizer?.phone_number || "",
      email: existingOrganizer?.email || "",
      country: existingOrganizer?.country || "",
      city: existingOrganizer?.city || "",
      logo: null,
    },
    validate,
    onSubmit: async (values) => {
      setIsSubmitting(true);

      try {
        const normalizedPhoneNumber = normalizePhoneNumber(values.phoneNumber);
        let logoValue = values.logo;
        if (isEditMode && !logoChangedRef.current && existingOrganizer?.logo) {
          logoValue = existingOrganizer.logo;
        }
        
        // Build payload
        const payload = {
          name: values.name.trim(),
          phone_number: normalizedPhoneNumber,
          email: values.email.trim().toLowerCase(),
          country: values.country.trim(),
          city: values.city.trim(),
          logo: logoValue,
        };
        if (isEditMode) {
          const response = await organizerService.updateOrganizer(
            existingOrganizer?.slug || "",
            payload
          );
          if (!response.status) {
            toast.error(
              response.message || "An error occurred. Please try again."
            );
            return;
          }
          toast.success(response?.data?.message);
        } else {
          const response = await organizerService.createOrganizer(payload);
          if (!response.status) {
            toast.error(
              response.message || "An error occurred. Please try again."
            );
            return;
          }
          toast.success(response?.data?.message);
        }
        setSavedOrganizerName(values.name);
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate("/admin/media/organizer");
        }, 3000);
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error(
          error.response?.data?.reason || "An error occurred. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Handle success modal close
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/admin/media/organizer");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="px-3 py-3 mx-auto max-w-8xl sm:px-4 sm:py-4 lg:px-6 lg:py-5">
        {/* Header */}
        <div className="mb-4">
          <button
            onClick={() => navigate("/admin/media/organizer")}
            className="flex items-center mb-3 text-gray-600 transition-colors hover:text-gray-900 group"
          >
            <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
            <span className="text-sm font-medium">Back to Organizers</span>
          </button>

          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
                {isEditMode ? "Edit Organizer" : "Add New Organizer"}
              </h1>
              <p className="mt-0.5 text-xs text-gray-600">
                {isEditMode
                  ? "Update organizer information"
                  : "Fill in the details to create a new organizer"}
              </p>
            </div>
          </div>
        </div>

        {/* UX Guidelines Banner */}
        <div className="p-3 mb-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-800">
              <p className="mb-0.5 font-semibold">💡 Quick Tips:</p>
              <ul className="space-y-0.5 text-xs list-disc list-inside">
                <li>Use high-quality logos (PNG or JPG, max 5MB)</li>
                <li>Phone numbers can be local (0249158582) or international (+233249158582)</li>
                <li>All fields marked with * are required</li>
                <li>Double-check email addresses for accuracy</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="space-y-4">
          {/* Main Information Card */}
          <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
            <h2 className="flex items-center mb-4 text-base font-bold text-gray-900">
              <Building2 className="mr-2 w-4 h-4 text-blue-600" />
              Organizer Information
            </h2>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Organizer Name */}
              <div className="sm:col-span-2">
                <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                  Organizer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="e.g., Global Events Company"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    formik.touched.name && formik.errors.name
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 bg-gray-50 focus:bg-white"
                  }`}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="flex items-center mt-1 text-xs text-red-600">
                    <AlertCircle className="mr-1 w-3 h-3" />
                    {formik.errors.name}
                  </div>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-2.5 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                  <input
                    type="tel"
                    name="phoneNumber"
                    placeholder="0249158582, +233249158582, or +233 24 123 4567"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      formik.touched.phoneNumber && formik.errors.phoneNumber
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200 bg-gray-50 focus:bg-white"
                    }`}
                  />
                </div>
                {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                  <div className="flex items-center mt-1 text-xs text-red-600">
                    <AlertCircle className="mr-1 w-3 h-3" />
                    {formik.errors.phoneNumber}
                  </div>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                  <input
                    type="email"
                    name="email"
                    placeholder="contact@example.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      formik.touched.email && formik.errors.email
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200 bg-gray-50 focus:bg-white"
                    }`}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <div className="flex items-center mt-1 text-xs text-red-600">
                    <AlertCircle className="mr-1 w-3 h-3" />
                    {formik.errors.email}
                  </div>
                )}
              </div>

              {/* Country */}
              <div>
                <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                  Country <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Globe className="absolute left-2.5 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                  <input
                    type="text"
                    name="country"
                    placeholder="e.g., Ghana, Nigeria, United States"
                    value={formik.values.country}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      formik.touched.country && formik.errors.country
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200 bg-gray-50 focus:bg-white"
                    }`}
                  />
                </div>
                {formik.touched.country && formik.errors.country && (
                  <div className="flex items-center mt-1 text-xs text-red-600">
                    <AlertCircle className="mr-1 w-3 h-3" />
                    {formik.errors.country}
                  </div>
                )}
              </div>

              {/* City */}
              <div>
                <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                  City <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-2.5 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                  <input
                    type="text"
                    name="city"
                    placeholder="e.g., Accra"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      formik.touched.city && formik.errors.city
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200 bg-gray-50 focus:bg-white"
                    }`}
                  />
                </div>
                {formik.touched.city && formik.errors.city && (
                  <div className="flex items-center mt-1 text-xs text-red-600">
                    <AlertCircle className="mr-1 w-3 h-3" />
                    {formik.errors.city}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Logo Upload Card */}
          <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
            <h2 className="flex items-center mb-2 text-base font-bold text-gray-900">
              <ImageIcon className="mr-2 w-4 h-4 text-blue-600" />
              Organizer Logo
            </h2>
            <p className="mb-4 text-xs text-gray-600">
              Upload a logo for the organizer. Recommended size: 512x512px (PNG,
              JPG)
            </p>

            <ImageUpload
              value={formik.values.logo}
              onChange={(value) => {
                logoChangedRef.current = true;
                formik.setFieldValue("logo", value);
              }}
              previewUrl={existingOrganizer?.logo}
              maxSize={5}
              acceptedFormats={[
                "image/jpeg",
                "image/jpg",
                "image/png",
                "image/webp",
              ]}
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin/media/organizer")}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 text-sm text-gray-700 bg-white border-2 border-gray-200 hover:bg-gray-50 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formik.isValid}
              className="flex-1 px-4 py-2.5 text-sm bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  {isEditMode ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Check className="mr-2 w-4 h-4" />
                  {isEditMode ? "Update Organizer" : "Create Organizer"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title={isEditMode ? "Successfully Updated!" : "Successfully Created!"}
        message={
          <>
            <span className="font-semibold text-gray-900">
              {savedOrganizerName}
            </span>{" "}
            has been {isEditMode ? "updated" : "added"} to the organizers list.
          </>
        }
        autoRedirectMessage="Redirecting to organizers list in a moment..."
        buttonText="View Organizers List"
      />
    </div>
  );
};

export default CreateEditOrganizer;
